<script lang="ts">
  import type { HTMLAttributes } from 'vue'

  export type ComboboxFieldModelValue = string | undefined

  export type ComboboxFieldItem<T = undefined> = T extends undefined
    ? {
        title: string
        value: string
        searchValue?: string
        dataTestid?: (item: ComboboxFieldItem<T>) => string
      }
    : {
        title: string
        value: string
        searchValue?: string
        metaData: T
      }

  export interface ComboboxFieldProps<T = undefined> {
    modelValue: ComboboxFieldModelValue
    items: ComboboxFieldItem<T>[]
    /** Forwarded to the trigger `id` for `<label for>` association. */
    id?: string
    /**
     * Renders the trigger as a stacked field-box with the label inside
     * on top (DatePicker-style). Without it, label/error chrome stays
     * the call site's job (e.g. wrap in `FormField`).
     */
    label?: string
    placeholder?: string
    showSearch?: boolean
    /**
     * When set, the clear button is hidden (maps to SelectInput
     * `clearable = !required`). A string is accepted for source-compat with
     * the old field API but is treated the same as `true`.
     */
    required?: boolean | string
    disabled?: boolean
    loading?: boolean
    dataTestid?: string
    class?: HTMLAttributes['class']
    inputClass?: HTMLAttributes['class']
    popoverProps?: {
      align?: 'start' | 'center' | 'end'
      placeholderSearch?: string
      noItemText?: string
      class?: HTMLAttributes['class']
      width?: number
    }
  }

  export type ComboboxFieldEmits = {
    (e: 'update:modelValue', val: ComboboxFieldModelValue): void
  }

  export type ComboboxFieldSlots<T = undefined> = {
    trigger: (props: { selectedTitle: string | undefined }) => void
    default: void
    item: (props: { item: ComboboxFieldItem<T>; selected: boolean }) => void
    content: void
    'prepend-content-item': void
    'append-content-item': void
  }
</script>

<script setup lang="ts" generic="T = undefined">
  import { computed } from 'vue'
  import { cn } from '../../lib/utils'
  import { SelectInput, type SelectInputItem } from '../ui/select-input'

  /**
   * Button-trigger (click-to-open) combobox. A thin pure control over
   * `SelectInput` — it adds nothing of its own beyond mapping its item shape
   * and forwarding slots; it carries no form-field chrome. For label / error
   * chrome, wrap it in a `FormField` (`input-group="false"`) at the call site.
   */
  defineOptions({ inheritAttrs: false })

  defineSlots<ComboboxFieldSlots<T>>()

  const props = withDefaults(defineProps<ComboboxFieldProps<T>>(), {
    showSearch: true
  })

  const emit = defineEmits<ComboboxFieldEmits>()

  const open = defineModel<boolean>('open', { default: false })

  const clearable = computed(() => !props.required)

  // ComboboxFieldItem is structurally a SelectInputItem (value/title/
  // searchValue/metaData); the cast keeps the public item shape while reusing
  // SelectInput's rendering.
  const selectItems = computed(() => props.items as SelectInputItem<T>[])

  function toComboboxItem(item: SelectInputItem<T>): ComboboxFieldItem<T> {
    return item as unknown as ComboboxFieldItem<T>
  }

  function onUpdate(value: string) {
    emit('update:modelValue', value === '' ? undefined : value)
  }
</script>

<template>
  <SelectInput
    :id="id"
    v-model:open="open"
    :label="label"
    :model-value="modelValue"
    :items="selectItems"
    :placeholder="placeholder"
    :show-search="showSearch"
    :clearable="clearable"
    :disabled="disabled"
    :loading="loading"
    :data-testid="dataTestid"
    :input-class="cn(props.class, inputClass)"
    :popover-props="popoverProps"
    @update:model-value="onUpdate"
  >
    <template v-if="$slots.trigger" #trigger="{ item }">
      <slot name="trigger" :selected-title="item?.title" />
    </template>
    <template v-if="$slots.default" #selected>
      <slot />
    </template>
    <template v-if="$slots.item" #item="{ item, selected }">
      <slot name="item" :item="toComboboxItem(item)" :selected="selected" />
    </template>
    <template v-if="$slots.content" #content>
      <slot name="content" />
    </template>
    <template v-if="$slots['prepend-content-item']" #prepend-content-item>
      <slot name="prepend-content-item" />
    </template>
    <template v-if="$slots['append-content-item']" #append-content-item>
      <slot name="append-content-item" />
    </template>
  </SelectInput>
</template>
